import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { MdsOption } from '@medpacesoftwaredevelopment/designsystem';

export const passwordValidatorPattern: string =
    '^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[\\*\\.\\!\\@\\$\\%\\^\\&\\(\\)\\{\\}\\[\\]\\:;\\<\\>,\\.\\?\\/\\~_\\+\\-\\=\\|]).{8,256}$';

export const matToolTipTextPassword: string =
    'Password must contain at least one uppercase letter, one lowercase letter, one digit, one special character(?=.*[*.!@$%^&(){}[]:;<>,.?/~_+-=|]), and must be between 8 to 256 characters in length.';

export const validationMessages = {
    notValidDate: 'Specify valid date!',
    invalidPastDate: 'Past dates are not allowed',
    invalidFutureDate: 'Future dates are not allowed',
    invalidDate: 'Invalid date',
    required: 'Required!',
    countryName: 'Invalid country name',
};

export function isValidCountry(array: MdsOption[]): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (array.some((x) => x.value === control.value?.value)) {
            return null;
        } else {
            return { inArray: validationMessages.countryName };
        }
    };
}

export const NumberFormatValidator: ValidatorFn = Validators.pattern('[0-9]+');

const phoneNumberFormatValidator: ValidatorFn = Validators.pattern(
    '^(([+][0-9]{1,3}[ ]?)|([(][+][0-9]{1,3}[)][ ]?))?((([0-9]{2,4}[ ]?){2,4})|(([0-9]{2,4}[-]?){2,4}))[0-9]{2,4}$'
);

// Workaround needed until MDS fixes rendering of more than 1 validation message.
// Ultimately, we should be able to use two existing validators: phoneNumberFormatValidator and maxLengthValidator, and not need that one.
export function phoneNumberFormatValidatorWithMaxLength(maxLength: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const phoneNumberFormatValidationResult = phoneNumberFormatValidator(control);
        const lengthValidationResult = Validators.maxLength(maxLength)(control);

        if (phoneNumberFormatValidationResult || lengthValidationResult)
            return {
                invalidPhoneNumber: `Value must be a valid phone number, no longer than ${maxLength} characters.`,
            };
        return null;
    };
}

export const zipCodeFormatValidator: ValidatorFn = Validators.maxLength(10);

/**
 * Validator that checks if there are multiple controls with the same value in formGroup
 * @param formGroup FormGroup that is validated
 * @param message message that will be shown in frontend
 * @returns object { inArray: message } if there are multiple FormControls with the same value, else null
 */
export function duplicateInFormGroupValidator(formGroup: FormGroup, message: string): ValidatorFn {
    return (control: AbstractControl<string>): { [key: string]: any } | null => {
        formGroup.updateValueAndValidity({ emitEvent: false });
        const array: string[] = Object.values(formGroup.value);
        if (array.length > 0 && !!control.value) {
            const isDuplicate =
                array.filter((option) => option?.toLowerCase() === control.value?.toLowerCase()).length > 1;
            //if array has duplicate, return validation error
            if (isDuplicate) {
                control.markAsTouched();
                return { inArray: message }; // Value is duplicated in an array
            } else return null;
        } else return null;
    };
}

/**
 * Validator that checks if there are multiple controls with the same value in formGroup
 * @param formGroup FormGroup that is validated
 * @param message message that will be shown in frontend
 * @returns object { inArray: message } if there are multiple FormControls with the same value, else null
 */
export function duplicateFormGroupInFormGroupValidator(formGroup: FormGroup, message: string): ValidatorFn {
    return (control: AbstractControl<{ name: string; scheduled: boolean }>): { [key: string]: any } | null => {
        formGroup.updateValueAndValidity({ emitEvent: false });

        const array: { name: string; scheduled: boolean }[] = Object.values(formGroup.value);

        const value: { name: string; scheduled: boolean } = control.value;
        if (array.length > 0 && !!value) {
            const isDuplicate =
                array.filter((option) => option?.name?.toLowerCase() === value?.name?.toLowerCase()).length > 1;
            //if array has duplicate, return validation error
            if (isDuplicate) {
                control.get('name').setErrors({ inArray: message });
                control.get('name').markAllAsTouched();
                return { inArray: message };
            } else {
                control.get('name').updateValueAndValidity({ onlySelf: true });
                return null;
            }
        } else return null;
    };
}

export function sameAddressValidator(toggleControlName: string, groupControlName: string): ValidatorFn {
    return (form: AbstractControl): ValidationErrors | null => {
        const group = form as FormGroup;
        const sameAddress = group.get(toggleControlName)?.value;
        const targetGroup = group.get(groupControlName) as FormGroup;
        if (!targetGroup) return null;

        if (sameAddress === 'true') {
            Object.keys(targetGroup.controls).forEach((key) => {
                const control = targetGroup.get(key);
                if (control?.errors) control.setErrors(null);
            });
            return null;
        }

        return targetGroup.valid ? null : { [groupControlName + 'Invalid']: true };
    };
}

// Validator similar to Angular's built-in Validators.maxLength, but with the added value of a human-readable error message required by MDS controls
export function maxLengthValidator(maxLength: number): ValidatorFn {
    return (control: AbstractControl<string>): ValidationErrors | null => {
        const maxLengthValidationResult = Validators.maxLength(maxLength)(control);
        if (maxLengthValidationResult) {
            return { maxLengthExceeded: `Value must be at most ${maxLength} characters long.` };
        }
        return null;
    };
}

export function minLengthValidator(minLength: number): ValidatorFn {
    return (control: AbstractControl<string>): ValidationErrors | null => {
        const minLengthValidationResult = Validators.minLength(minLength)(control);
        if (minLengthValidationResult) {
            return { minLengthExceeded: `Value must be at least ${minLength} characters long.` };
        }
        return null;
    };
}

/** A wrapper over @angular/forms/Validators.email, extended with a clear error message, to make it compatible with MDS controls */
export function emailValidator(control: AbstractControl<string>): ValidationErrors | null {
    // Validators.email is disallowed in the project, but here we intentionally want to use it so we can create an MDS-compatible wrapper
    // eslint-disable-next-line no-restricted-syntax
    const emailFormatValidationResult = Validators.email(control);

    if (emailFormatValidationResult) {
        return { invalidEmailFormat: `Invalid email address format.` };
    }
    return null;
}

const millisecondsPerDay = 24 * 60 * 60 * 1000;

function isEmptyDateValue(value: unknown): boolean {
    return value === null || value === undefined || value === '';
}

function toMomentCalendarDay(value: unknown): number | null {
    if (typeof value !== 'object' || value === null) return null;

    const momentValue = value as {
        isValid?: () => boolean;
        year?: () => number;
        month?: () => number;
        date?: () => number;
    };

    if (
        typeof momentValue.year !== 'function' ||
        typeof momentValue.month !== 'function' ||
        typeof momentValue.date !== 'function'
    )
        return null;
    if (typeof momentValue.isValid === 'function' && !momentValue.isValid()) return null;

    return Date.UTC(momentValue.year(), momentValue.month(), momentValue.date()) / millisecondsPerDay;
}

function coerceToDate(value: unknown): Date | null {
    if (isEmptyDateValue(value)) return null;

    if (value instanceof Date) return value;

    if (typeof value === 'object' && value !== null && 'toDate' in value && typeof value.toDate === 'function') {
        return value.toDate();
    }

    if (typeof value === 'string') {
        const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
        if (dateOnlyMatch) {
            const year = Number(dateOnlyMatch[1]);
            const monthIndex = Number(dateOnlyMatch[2]) - 1;
            const day = Number(dateOnlyMatch[3]);
            const date = new Date(year, monthIndex, day);

            if (date.getFullYear() !== year || date.getMonth() !== monthIndex || date.getDate() !== day) {
                return null;
            }

            return date;
        }
    }

    return new Date(value as string | number);
}

function toLocalCalendarDay(value: unknown): number | null {
    const momentCalendarDay = toMomentCalendarDay(value);
    if (momentCalendarDay !== null) return momentCalendarDay;

    const date = coerceToDate(value);
    if (!date || Number.isNaN(date.getTime())) return null;

    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / millisecondsPerDay;
}

function todayLocalCalendarDay(): number {
    return toLocalCalendarDay(new Date()) as number;
}

const minSupportedDateCalendarDay = toLocalCalendarDay(new Date(1900, 0, 1)) as number;
const maxSupportedDateCalendarDay = toLocalCalendarDay(new Date(9999, 11, 31)) as number;

export function equalOrLessThanTodayDateValidator(control: AbstractControl): null | { notValidDate: string } {
    const calendarDay = toLocalCalendarDay(control.value);
    if (calendarDay === null)
        return isEmptyDateValue(control.value) ? null : { notValidDate: validationMessages.invalidDate };
    if (calendarDay < minSupportedDateCalendarDay) return { notValidDate: validationMessages.invalidDate };
    if (calendarDay > todayLocalCalendarDay()) return { notValidDate: validationMessages.invalidFutureDate };

    return null;
}

export function moreThanTodayDateValidator(control: AbstractControl): null | { notValidDate: string } {
    const calendarDay = toLocalCalendarDay(control.value);
    if (calendarDay === null)
        return isEmptyDateValue(control.value) ? null : { notValidDate: validationMessages.invalidDate };
    if (calendarDay > maxSupportedDateCalendarDay) return { notValidDate: validationMessages.invalidDate };
    if (calendarDay <= todayLocalCalendarDay()) return { notValidDate: validationMessages.invalidPastDate };

    return null;
}

export function equalOrMoreThanTodayOnlyDateValidator(control: AbstractControl): null | { notValidDate: string } {
    const calendarDay = toLocalCalendarDay(control.value);
    if (calendarDay === null)
        return isEmptyDateValue(control.value) ? null : { notValidDate: validationMessages.invalidDate };

    return calendarDay >= todayLocalCalendarDay() ? null : { notValidDate: validationMessages.invalidPastDate };
}
export function equalOrMoreThanTodayOnlyIfCRCDateValidator(control: AbstractControl): ValidationErrors | null {
    if (!UserServiceV2.isCrc()) return null;
    return equalOrMoreThanTodayOnlyDateValidator(control);
}

export function beforeDateValidator(maxdate: Date, message: string): ValidatorFn {
    return (control: AbstractControl): null | { notValidDate: string } => {
        const calendarDay = toLocalCalendarDay(control.value);
        const maxCalendarDay = toLocalCalendarDay(maxdate);
        if (calendarDay === null)
            return isEmptyDateValue(control.value) ? null : { notValidDate: validationMessages.invalidDate };
        if (maxCalendarDay === null || calendarDay <= maxCalendarDay) return null;
        return { notValidDate: message };
    };
}

export function afterDateValidator(minDate: Date, message: string): ValidatorFn {
    return (control: AbstractControl): null | { notValidDate: string } => {
        const calendarDay = toLocalCalendarDay(control.value);
        const minCalendarDay = toLocalCalendarDay(minDate);
        if (calendarDay === null)
            return isEmptyDateValue(control.value) ? null : { notValidDate: validationMessages.invalidDate };
        if (minCalendarDay === null || calendarDay >= minCalendarDay) return null;
        return { notValidDate: message };
    };
}

export function passwordMatchValidator(message: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const parent = control.parent;
        if (!parent) return null;

        const password = parent.get('password')?.value;

        const confirmPassword = control.value;

        if (!password || !confirmPassword) return null;

        return password === confirmPassword ? null : { notSameString: message };
    };
}

export function disallowedValueValidator<T>(
    disallowedValue: T,
    isDisallowedValueAllowed: boolean,
    message: string
): ValidatorFn {
    return (control: AbstractControl<T>): ValidationErrors | null => {
        if (Validators.required(control) === null && (control.value !== disallowedValue || isDisallowedValueAllowed))
            return null;

        return { required: message };
    };
}

export function requiredValidatorCustomMessage(message?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!!message) {
            const validationResult = Validators.required(control);
            if (!!validationResult) {
                return { required: message };
            }
        } else return Validators.required(control);
        return null;
    };
}

export function requiredAtLeastOneOption(formGroup: FormGroup): ValidationErrors | null {
    if (!!formGroup) {
        //count elements in formGroup
        const keys = Object.keys(formGroup.value);
        return keys.length === 0 ? <ValidationErrors>{ required: 'At least one option is required' } : null;
    }
    return null;
}

export function requiredAtLeastOneOptionArray(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const formArray = control as FormArray;
        if (!!formArray) {
            //count elements in formArray
            return formArray.length === 0 ? <ValidationErrors>{ required: 'At least one option is required' } : null;
        }
        return null;
    };
}

export function requiredAtLeastOneOptionCheckedArray(control: AbstractControl): ValidationErrors | null {
    if (!!control) {
        const formArray = control as FormArray;
        const atLeastOneChecked = formArray.controls.some((ctrl) => ctrl.value == true);

        return atLeastOneChecked ? null : <ValidationErrors>{ required: 'At least one option checked is required' };
    }
    return null;
}

export function invalidValue(fallbackValue: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value && fallbackValue.length > 0) {
            return { invalid: 'Invalid format, expected DD-MON-YYYY' };
        }
        return null;
    };
}

export function manualValidator(handle: ValidatorHandle, validationErrors: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!handle.valid) {
            return validationErrors;
        }
        return null;
    };
}
export class ValidatorHandle {
    public valid: boolean = true;
    public formControl: AbstractControl;
    setValid(valid: boolean, opts: { markAsTouched?: boolean } = { markAsTouched: true }) {
        this.valid = valid;
        if (opts.markAsTouched) {
            this.formControl.markAsTouched();
        }
        this.formControl.updateValueAndValidity({
            onlySelf: true,
            emitEvent: true,
        });
    }
}

export function notWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        if (!value) return null;

        if (typeof value === 'string') {
            if (value.trim().length > 0) return null;

            return { notWhitespace: 'Value is empty' };
        }

        return null;
    };
}
